import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../lib/cart';
import { price, priceOrFree } from '../lib/format';
import { Alert, Button, ButtonLink, Spinner } from './ui';

/**
 * Slide-over basket.
 *
 * Every figure shown here comes from the server's pricing run — the drawer
 * never adds anything up itself, so what the shopper sees is exactly what
 * checkout will charge.
 */
export function CartDrawer() {
  const {
    cart,
    isOpen,
    close,
    loading,
    error,
    clearError,
    setQuantity,
    remove,
    applyCoupon,
    removeCoupon,
  } = useCart();

  const [couponInput, setCouponInput] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Close on Escape, and lock background scrolling while open.
  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, close]);

  if (!isOpen) return null;

  const totals = cart?.totals;
  const lines = totals?.lines ?? [];
  const currency = totals?.currency ?? 'GBP';

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div
        className="absolute inset-0 bg-ink/40"
        onClick={close}
        aria-hidden="true"
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Shopping basket"
        className="relative flex h-full w-full max-w-md flex-col bg-background shadow-2xl"
      >
        <header className="flex items-center justify-between border-b border-border px-6 py-5">
          <h2 className="text-lg">
            Basket{' '}
            {totals && totals.totalQuantity > 0 && (
              <span className="text-sm text-muted">({totals.totalQuantity})</span>
            )}
          </h2>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={close}
            className="cursor-pointer p-1 text-lg"
            aria-label="Close basket"
          >
            ✕
          </button>
        </header>

        {error && (
          <div className="px-6 pt-4">
            <Alert onDismiss={clearError}>{error}</Alert>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {lines.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
              <p className="text-sm text-muted">Your basket is empty.</p>
              <Button variant="secondary" onClick={close}>
                Continue shopping
              </Button>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {lines.map((line) => (
                <li key={line.productId} className="flex gap-4 py-4">
                  <Link
                    to={`/products/${line.slug}`}
                    onClick={close}
                    className="h-24 w-20 shrink-0 overflow-hidden bg-surface"
                  >
                    {line.image && (
                      <img
                        src={line.image.src}
                        alt={line.image.alt}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    )}
                  </Link>

                  <div className="flex flex-1 flex-col gap-1">
                    <Link
                      to={`/products/${line.slug}`}
                      onClick={close}
                      className="text-xs leading-snug hover:underline"
                    >
                      {line.title.split('|')[0]?.trim() ?? line.title}
                    </Link>

                    <p className="text-xs text-muted">
                      {price(line.unitPriceMinor, currency)} each
                    </p>

                    {line.discounts.length > 0 && (
                      <p className="text-[10px] uppercase tracking-wider text-sale">
                        {line.discounts.map((d) => d.title).join(' · ')}
                      </p>
                    )}

                    {line.exceedsStock && (
                      <p className="text-[11px] text-error">
                        Only {line.availableStock} left — please reduce the quantity.
                      </p>
                    )}

                    <div className="mt-1 flex items-center justify-between">
                      <div className="flex items-center border border-border">
                        <button
                          type="button"
                          className="cursor-pointer px-2.5 py-1 text-sm disabled:opacity-30"
                          onClick={() => void setQuantity(line.productId, line.quantity - 1)}
                          disabled={loading}
                          aria-label={`Reduce quantity of ${line.title}`}
                        >
                          −
                        </button>
                        <span className="min-w-8 text-center text-xs">{line.quantity}</span>
                        <button
                          type="button"
                          className="cursor-pointer px-2.5 py-1 text-sm disabled:opacity-30"
                          onClick={() => void setQuantity(line.productId, line.quantity + 1)}
                          // One-of-a-kind pieces: stop at available stock.
                          disabled={loading || line.quantity >= line.availableStock}
                          aria-label={`Increase quantity of ${line.title}`}
                        >
                          +
                        </button>
                      </div>

                      <div className="text-right">
                        <p className="text-sm">{price(line.lineTotalMinor, currency)}</p>
                        <button
                          type="button"
                          onClick={() => void remove(line.productId)}
                          className="cursor-pointer text-[11px] text-muted underline underline-offset-2 hover:text-ink"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {lines.length > 0 && totals && (
          <footer className="border-t border-border px-6 py-5">
            <form
              className="mb-4 flex gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                if (couponInput.trim()) {
                  void applyCoupon(couponInput.trim()).then(() => setCouponInput(''));
                }
              }}
            >
              <input
                type="text"
                value={couponInput}
                onChange={(event) => setCouponInput(event.target.value)}
                placeholder="Discount code"
                aria-label="Discount code"
                className="flex-1 border border-border bg-transparent px-3 py-2 text-xs uppercase outline-none focus:border-ink"
              />
              <Button type="submit" variant="secondary" size="sm" disabled={loading}>
                Apply
              </Button>
            </form>

            {totals.couponError && (
              <p className="mb-3 text-xs text-error">{totals.couponError}</p>
            )}

            {cart?.couponCode && !totals.couponError && (
              <p className="mb-3 flex items-center gap-2 text-xs text-success">
                Code {cart.couponCode} applied
                <button
                  type="button"
                  onClick={() => void removeCoupon()}
                  className="cursor-pointer text-muted underline underline-offset-2"
                >
                  remove
                </button>
              </p>
            )}

            <dl className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <dt className="text-muted">Subtotal</dt>
                <dd>{price(totals.subtotalMinor, currency)}</dd>
              </div>

              {totals.discountMinor > 0 && (
                <div className="flex justify-between text-sale">
                  <dt>Discounts</dt>
                  <dd>−{price(totals.discountMinor, currency)}</dd>
                </div>
              )}

              <div className="flex justify-between">
                <dt className="text-muted">Delivery</dt>
                <dd>
                  {totals.shippingDiscountMinor > 0 ? (
                    <>
                      <span className="mr-1 text-muted line-through">
                        {price(totals.shippingMinor, currency)}
                      </span>
                      Free
                    </>
                  ) : (
                    priceOrFree(totals.shippingMinor, currency)
                  )}
                </dd>
              </div>

              <div className="flex justify-between border-t border-border pt-2 text-base">
                <dt>Total</dt>
                <dd className="font-bold">{price(totals.totalMinor, currency)}</dd>
              </div>
            </dl>

            <ButtonLink
              to="/checkout"
              size="lg"
              className="mt-4 w-full"
              onClick={close}
            >
              {loading ? <Spinner /> : null}
              Checkout
            </ButtonLink>

            <button
              type="button"
              onClick={close}
              className="mt-3 w-full cursor-pointer text-center text-xs text-muted underline underline-offset-4"
            >
              Continue shopping
            </button>
          </footer>
        )}
      </div>
    </div>
  );
}
